import React from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateBreadcrumbSchema } from '../../utils/seo';

const LocationPage: React.FC = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'DMV Services Location Las Vegas - 4525 S Sandhill Rd | Speedy DMV',
    description: 'Find Speedy DMV Services at 4525 S Sandhill Rd, Las Vegas NV 89121. Convenient location serving all of Clark County Nevada. Mon–Fri 9–5, Sat 10–3. Call 702.979.7202.',
    keywords: 'DMV location Las Vegas, Speedy DMV address, DMV services Sandhill Las Vegas, Nevada DMV near me',
    canonical: `${siteUrl}/location`,
    ogImage: `${siteUrl}/img/speedy-dmv-services.jpg`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        {
          "@context": "https://schema.org",
          "@type": "Place",
          "name": "Speedy DMV Services",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "4525 S Sandhill Rd",
            "addressLocality": "Las Vegas",
            "addressRegion": "NV",
            "postalCode": "89121",
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 36.0840,
            "longitude": -115.0677
          },
          "url": `${siteUrl}/location`
        },
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Location', url: '/location' }
        ])
      ]
    }
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-20 pb-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold mb-4">Our Location</h1>
              <p className="text-xl text-blue-100">
                Visit our convenient Las Vegas location for all your DMV service needs
              </p>
            </div>
          </div>
        </section>

        {/* Location Info */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Address & Contact */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Visit Us Today</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <i className="ri-map-pin-line text-blue-600 text-xl"></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Address</h3>
                      <p className="text-gray-600">
                        4525 S Sandhill Rd<br />
                        Las Vegas, NV 89121
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <i className="ri-phone-line text-blue-600 text-xl"></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Phone</h3>
                      <p className="text-gray-600">(702) 979-7202</p>
                      <p className="text-sm text-red-600 font-medium">Appointments Required - Please Call Ahead</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <i className="ri-time-line text-blue-600 text-xl"></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Business Hours</h3>
                      <div className="text-gray-600">
                        <p>Monday - Friday: 8:00 AM - 5:00 PM</p>
                        <p>Saturday: 9:00 AM - 3:00 PM</p>
                        <p>Sunday: Closed</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <a
                    href="tel:7029797202"
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition duration-300 flex items-center justify-center"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Call Now to Schedule
                  </a>
                </div>
              </div>

              {/* Map */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="h-96">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3218.7755!2d-115.0735!3d36.0826!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c8c7a0cf0cc689%3A0x8b5c8c5b8c5b8c5b!2s4525%20S%20Sandhill%20Rd%2C%20Las%20Vegas%2C%20NV%2089121!5e0!3m2!1sen!2sus!4v1635959999999!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Speedy DMV Services Location"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Directions */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Directions & Parking</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  <i className="ri-car-line text-blue-600 mr-2"></i>
                  Driving Directions
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• From I-215: Take Exit 12 for Sandhill Road South</li>
                  <li>• From Las Vegas Boulevard: Head East on Flamingo, turn South on Sandhill</li>
                  <li>• From Henderson: Take I-215 West to Sandhill Road Exit</li>
                  <li>• Look for our sign at 4525 S Sandhill Road</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  <i className="ri-parking-line text-blue-600 mr-2"></i>
                  Parking Information
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Free parking available on-site</li>
                  <li>• Wheelchair accessible parking spaces</li>
                  <li>• Well-lit and secure parking area</li>
                  <li>• Easy access to our office entrance</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Our Location */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Why Our Location Works for You</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-map-2-line text-blue-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Convenient Location</h3>
                <p className="text-gray-600">
                  Easily accessible from major Las Vegas areas with ample parking and highway access.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-line text-blue-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Extended Hours</h3>
                <p className="text-gray-600">
                  Open Monday through Saturday with flexible scheduling to fit your busy life.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-blue-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Safe & Secure</h3>
                <p className="text-gray-600">
                  Professional office environment with secure document handling and privacy protection.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default LocationPage;